package dev.danko.roblox_craft_ai.uikit.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.ObjectFit
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import dev.danko.roblox_craft_ai.shared.model.AssetResponse
import dev.danko.roblox_craft_ai.shared.model.AssetRevision
import dev.danko.roblox_craft_ai.shared.model.MeshEditRequest
import dev.danko.roblox_craft_ai.shared.model.MeshInfo
import dev.danko.roblox_craft_ai.shared.model.RobloxMeshLimits
import dev.danko.roblox_craft_ai.uikit.config.BackendEndpoint
import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import dev.danko.roblox_craft_ai.uikit.i18n.MeshInspectorStrings
import dev.danko.roblox_craft_ai.shared.model.RetextureRequest
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import org.jetbrains.compose.web.css.DisplayStyle
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Text
import kotlin.js.Date
import kotlin.math.abs
import kotlin.math.roundToInt

/** What the site provides so the panel can talk to the server. */
interface MeshToolsActions {
    suspend fun info(assetId: String): MeshInfo?
    /** Applies the fixes and returns the updated work; throws with a readable message on failure. */
    suspend fun edit(assetId: String, request: MeshEditRequest): AssetResponse
    suspend fun restore(assetId: String, revisionNumber: Int)
    fun objZipUrl(assetId: String): String
    /** The current version with a 2048 px texture, baked on request from the detailed source. */
    fun sharpGlbUrl(assetId: String): String
    /** «Сменить текстуру»: paints the model in a new style as a new version; throws with a readable message. */
    suspend fun retexture(assetId: String, request: RetextureRequest): AssetResponse
    /** Credit price of a retexture as a label, null when unknown. */
    fun retextureCost(): String?
    /** Puts a template R15 skeleton into the model; returns the updated work. */
    suspend fun rig(assetId: String): AssetResponse
    /** Meshy's finer skeleton (credits); returns the updated work. */
    suspend fun rigPro(assetId: String): AssetResponse
    fun rigProCost(): String?
    /** Changes a finished animation by an instruction; returns the updated work. */
    suspend fun editAnimation(assetId: String, instruction: String): AssetResponse
    suspend fun restoreAnimation(assetId: String, revisionNumber: Int): AssetResponse
    fun animationEditCost(): String?
}

enum class CheckLevel { OK, WARN, FAIL, PENDING }

/** One line of the Roblox checklist; [fix] is null when there is nothing to press. */
class MeshCheck(val level: CheckLevel, val text: String, val hint: String? = null, val fixLabel: String? = null, val fix: (() -> Unit)? = null)

private enum class InspectorTab { INFO, VIEW, TOOLS }

/**
 * Info, view and tools under the 3D viewer: the Roblox checklist with one-press fixes, how the model is shown, and
 * fixes that make new versions. Everything Roblox-specific (studs, the 20k triangle limit, -Z front) lives here.
 */
@Composable
fun MeshInspector(
    asset: AssetResponse,
    view: MeshViewState,
    info: MeshInfo?,
    busy: Boolean,
    onEdit: (MeshEditRequest) -> Unit,
    onRestore: (Int) -> Unit,
    onCompare: (AssetRevision) -> Unit,
    onDownloadGlb: () -> Unit,
    onDownloadObj: () -> Unit,
    onDownloadSharp: () -> Unit,
    onRetexture: (RetextureRequest) -> Unit,
    retextureCost: String?,
    onRig: () -> Unit,
    onRigPro: () -> Unit,
    rigProCost: String?
) {
    val t = strings().meshInspector
    val palette = LocalAppTheme.current.colors
    var tab by remember(asset.id) { mutableStateOf(InspectorTab.INFO) }
    val stats = view.stats
    val editable = info?.editable == true && !busy

    Column(Modifier.fillMaxWidth().gap(12.px)) {
        Row(Modifier.fillMaxWidth().gap(4.px).padding(3.px).borderRadius(10.px).backgroundColor(palette.background).border(1.px, LineStyle.Solid, palette.border)) {
            listOf(InspectorTab.INFO to t.tabInfo, InspectorTab.VIEW to t.tabView, InspectorTab.TOOLS to t.tabTools).forEach { (value, label) ->
                Segment(label, selected = tab == value, modifier = Modifier.flexGrow(1)) { tab = value }
            }
        }
        when (tab) {
            InspectorTab.INFO -> InfoTab(asset, stats, info, t, editable, onEdit) { tab = InspectorTab.TOOLS }
            InspectorTab.VIEW -> ViewTab(view, t)
            InspectorTab.TOOLS -> ToolsTab(asset, stats, info, t, busy, onEdit, onRestore, onCompare, onDownloadGlb, onDownloadObj, onDownloadSharp, onRetexture, retextureCost, onRig, onRigPro, rigProCost)
        }
    }
}

// ─── Checklist ──────────────────────────────────────────────────────────────────────────────────────────────────

fun meshChecks(
    stats: MeshModelStats,
    info: MeshInfo?,
    t: MeshInspectorStrings,
    canFix: Boolean,
    onEdit: (MeshEditRequest) -> Unit,
    openTools: () -> Unit
): List<MeshCheck> {
    if (!stats.loaded) return emptyList()
    fun fixOrNull(label: String, action: () -> Unit) = if (canFix) label to action else null
    val checks = mutableListOf<MeshCheck>()

    val triangles = info?.triangles ?: stats.triangles
    val max = RobloxMeshLimits.MAX_TRIANGLES
    checks += if (triangles <= max) MeshCheck(CheckLevel.OK, t.checkTriangles(triangles, max))
    else fixOrNull(t.fixSimplify) { onEdit(MeshEditRequest(targetTriangles = 18_000)) }.let {
        MeshCheck(CheckLevel.FAIL, t.checkTriangles(triangles, max), t.checkTrianglesOver, it?.first, it?.second)
    }

    val texture = info?.textureSize ?: stats.textureSize
    checks += when {
        texture <= 0 -> MeshCheck(CheckLevel.OK, t.checkTextureNone)
        texture <= RobloxMeshLimits.MAX_TEXTURE -> MeshCheck(CheckLevel.OK, t.checkTexture(texture))
        else -> fixOrNull(t.fixTexture) { onEdit(MeshEditRequest(maxTexture = RobloxMeshLimits.MAX_TEXTURE)) }.let {
            MeshCheck(CheckLevel.WARN, t.checkTexture(texture), t.checkTextureOver, it?.first, it?.second)
        }
    }

    checks += when {
        info?.frontSetByUser == true -> MeshCheck(CheckLevel.OK, t.checkFrontUser)
        info?.orientationYaw != null -> MeshCheck(CheckLevel.OK, t.checkFrontAuto)
        else -> MeshCheck(CheckLevel.WARN, t.checkFrontUnsure, null, t.fixRotate.takeIf { canFix }, openTools.takeIf { canFix })
    }

    val size = stats.sizeStuds
    if (size.size == 3) {
        val dims = size.joinToString(" × ") { t.studs(it).substringBefore(' ') } + " " + t.studs(size.maxOrNull() ?: 0.0).substringAfter(' ')
        val longest = size.maxOrNull() ?: 0.0
        checks += when {
            longest < RobloxMeshLimits.MIN_SIZE_STUDS -> MeshCheck(CheckLevel.WARN, t.checkSize(dims), t.checkSizeTiny, t.fixSize.takeIf { canFix }, openTools.takeIf { canFix })
            longest > RobloxMeshLimits.MAX_SIZE_STUDS -> MeshCheck(CheckLevel.WARN, t.checkSize(dims), t.checkSizeHuge, t.fixSize.takeIf { canFix }, openTools.takeIf { canFix })
            else -> MeshCheck(CheckLevel.OK, t.checkSize(dims))
        }
    }

    when {
        info == null -> checks += MeshCheck(CheckLevel.PENDING, t.checking)
        info.smallParts == null -> Unit // no mesh service: cannot tell
        info.smallParts == 0 -> checks += MeshCheck(CheckLevel.OK, t.checkPartsOk)
        else -> fixOrNull(t.fixDebris) { onEdit(MeshEditRequest(dropSmallParts = true)) }.let {
            checks += MeshCheck(CheckLevel.WARN, t.checkParts(info.smallParts ?: 0), null, it?.first, it?.second)
        }
    }

    val bottom = info?.bottomY
    val offset = info?.centerOffset
    if (bottom != null && offset != null) {
        val tolerance = maxOf(0.05, (size.maxOrNull() ?: 1.0) * 0.02)
        checks += if (abs(bottom) <= tolerance && offset <= tolerance) MeshCheck(CheckLevel.OK, t.checkPivotOk)
        else fixOrNull(t.fixPivot) { onEdit(MeshEditRequest(sizeStuds = size.maxOrNull())) }.let {
            MeshCheck(CheckLevel.WARN, t.checkPivotOff, null, it?.first, it?.second)
        }
    }
    return checks
}

@Composable
private fun InfoTab(
    asset: AssetResponse,
    stats: MeshModelStats,
    info: MeshInfo?,
    t: MeshInspectorStrings,
    editable: Boolean,
    onEdit: (MeshEditRequest) -> Unit,
    openTools: () -> Unit
) {
    val palette = LocalAppTheme.current.colors
    val checks = meshChecks(stats, info, t, editable, onEdit, openTools)
    val issues = checks.count { it.level == CheckLevel.WARN || it.level == CheckLevel.FAIL }

    Card {
        Row(Modifier.fillMaxWidth().gap(8.px), verticalAlignment = Alignment.CenterVertically) {
            SpanText(t.readinessTitle, Modifier.fontSize(14.px).fontWeight(FontWeight.Bold).color(palette.textPrimary))
            Spacer()
            if (checks.isNotEmpty()) {
                val color = if (issues == 0) palette.statusReady else palette.statusPending
                SpanText(
                    if (issues == 0) t.readyAll else t.readyIssues(issues),
                    Modifier.fontSize(12.px).fontWeight(FontWeight.SemiBold).color(color).padding(topBottom = 3.px, leftRight = 10.px)
                        .borderRadius(999.px).backgroundColor(color.toRgb().copy(alpha = 36))
                )
            }
        }
        if (checks.isEmpty()) SpanText(t.checking, Modifier.fontSize(13.px).color(palette.textSecondary))
        checks.forEach { CheckRow(it) }
    }

    Card {
        SectionTitle(t.geometry)
        val triangles = info?.triangles ?: stats.triangles
        InfoRow(t.triangles, "${t.number(triangles)} / ${t.number(RobloxMeshLimits.MAX_TRIANGLES)}")
        InfoRow(t.vertices, t.number(info?.vertices ?: stats.vertices))
        info?.parts?.let { InfoRow(t.parts, it.toString()) }
        if (stats.sizeStuds.size == 3) InfoRow(t.size, stats.sizeStuds.joinToString(" × ") { t.studs(it).substringBefore(' ') } + " " + t.studs(stats.sizeStuds.max()).substringAfter(' '))
        info?.fileBytes?.let { InfoRow(t.fileSize, t.megabytes(it)) }
        val texture = info?.textureSize ?: stats.textureSize
        InfoRow(t.texture, if (texture > 0) t.pixels(texture) else t.noTexture)
        InfoRow(t.reliefMap, if (info?.normalMap == true || stats.hasNormalMap) t.reliefYes else t.reliefNo)
    }

    Card {
        SectionTitle(t.origin)
        (info?.provider ?: asset.metadata["provider"])?.let { InfoRow(t.provider, providerName(it)) }
        if (asset.timestamp > 0) InfoRow(t.created, Date(asset.timestamp.toDouble()).toLocaleString(LocaleController.current.code))
        asset.metadata["currentRevision"]?.let { InfoRow(t.version, "v$it") }
        val sketch = asset.metadata["conceptImageUrl"] ?: asset.referenceImages.firstOrNull()
        if (sketch != null) {
            Row(Modifier.fillMaxWidth().gap(12.px), verticalAlignment = Alignment.CenterVertically) {
                SpanText(t.sketch, Modifier.fontSize(13.px).color(palette.textSecondary).width(120.px))
                Img(
                    src = BackendEndpoint.resolveAssetUrl(sketch),
                    attrs = Modifier.size(72.px).borderRadius(8.px).objectFit(ObjectFit.Cover).border(1.px, LineStyle.Solid, palette.border)
                        .toAttrs { attr("alt", t.sketch) }
                )
            }
        }
    }
}

private fun providerName(id: String): String = when {
    id == "fal-trellis-2" -> "TRELLIS 2 (fal.ai)"
    id.startsWith("fal-trellis") -> "TRELLIS (fal.ai)"
    id.startsWith("fal-hunyuan") -> "Hunyuan3D 3.1 Pro (fal.ai)"
    id.startsWith("local-tsr") -> "TripoSR"
    id.startsWith("meshy") -> "Meshy"
    id.startsWith("tripo") -> "Tripo"
    else -> id
}

@Composable
private fun CheckRow(check: MeshCheck) {
    val palette = LocalAppTheme.current.colors
    val (mark, color) = when (check.level) {
        CheckLevel.OK -> "✓" to palette.statusReady
        CheckLevel.WARN -> "!" to palette.statusPending
        CheckLevel.FAIL -> "✕" to palette.statusFailed
        CheckLevel.PENDING -> "…" to palette.textSecondary
    }
    Row(Modifier.fillMaxWidth().gap(10.px), verticalAlignment = Alignment.CenterVertically) {
        SpanText(
            mark,
            Modifier.size(22.px).flexShrink(0).borderRadius(50.percent).backgroundColor(color.toRgb().copy(alpha = 40)).color(color)
                .fontSize(12.px).fontWeight(FontWeight.Bold)
                .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
                .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center)
        )
        Column(Modifier.flexGrow(1).minWidth(0.px).gap(2.px)) {
            SpanText(check.text, Modifier.fontSize(13.px).color(palette.textPrimary))
            check.hint?.let { SpanText(it, Modifier.fontSize(12.px).color(palette.textSecondary)) }
        }
        val fix = check.fix
        if (check.fixLabel != null && fix != null) {
            AppButton(text = check.fixLabel, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, onClick = fix)
        }
    }
}

// ─── View ───────────────────────────────────────────────────────────────────────────────────────────────────────

@Composable
private fun ViewTab(view: MeshViewState, t: MeshInspectorStrings) {
    val v = strings().meshViewer
    Card {
        SectionTitle(t.mode)
        Chips {
            MeshShadingMode.values().forEach { mode ->
                Chip("${mode.icon} ${mode.localizedTitle(v)}", view.shading == mode) { view.shading = mode }
            }
        }
        SectionTitle(t.light)
        Chips {
            MeshLightingPreset.values().forEach { preset ->
                Chip("${preset.icon} ${preset.localizedTitle(v)}", view.lighting == preset) { view.lighting = preset }
            }
        }
        SectionTitle(t.background)
        Chips {
            MeshBackground.values().forEach { bg ->
                val label = when (bg) {
                    MeshBackground.DARK -> t.bgDark
                    MeshBackground.LIGHT -> t.bgLight
                    MeshBackground.GREY -> t.bgGrey
                    MeshBackground.BASEPLATE -> t.bgBaseplate
                }
                Chip(label, view.background == bg, swatch = Color.rgb(bg.color)) { view.background = bg }
            }
        }
    }
    Card {
        Toggle(t.reference, t.referenceHint, view.showReference) { view.showReference = it }
        if (view.stats.hasNormalMap) Toggle(t.relief, t.reliefHint, view.showRelief) { view.showRelief = it }
        Toggle(t.bounds, null, view.showBounds) { view.showBounds = it }
        Toggle(t.grid, null, view.showGrid) { view.showGrid = it }
        Toggle(t.spin, null, view.autoRotate) { view.autoRotate = it }
    }
    Card {
        Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap)) {
            AppButton(text = t.screenshot, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, onClick = {
                view.screenshotTransparent = false
                view.screenshotRequests++
            })
            AppButton(text = t.screenshotTransparent, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, onClick = {
                view.screenshotTransparent = true
                view.screenshotRequests++
            })
            AppButton(
                text = if (view.recording) t.videoRecording else t.video,
                variant = AppButtonVariant.SECONDARY,
                size = AppButtonSize.SM,
                loading = view.recording,
                enabled = !view.recording,
                onClick = { view.videoRequests++ }
            )
        }
    }
}

// ─── Tools ──────────────────────────────────────────────────────────────────────────────────────────────────────

@Composable
private fun ToolsTab(
    asset: AssetResponse,
    stats: MeshModelStats,
    info: MeshInfo?,
    t: MeshInspectorStrings,
    busy: Boolean,
    onEdit: (MeshEditRequest) -> Unit,
    onRestore: (Int) -> Unit,
    onCompare: (AssetRevision) -> Unit,
    onDownloadGlb: () -> Unit,
    onDownloadObj: () -> Unit,
    onDownloadSharp: () -> Unit,
    onRetexture: (RetextureRequest) -> Unit,
    retextureCost: String?,
    onRig: () -> Unit,
    onRigPro: () -> Unit,
    rigProCost: String?
) {
    val palette = LocalAppTheme.current.colors
    val canEdit = info?.editable == true && !busy
    val triangles = info?.triangles ?: stats.triangles

    if (busy) Note(t.working, palette.statusProcessing)
    else if (info != null && !info.editable) Note(t.unavailable, palette.statusPending)
    else SpanText(t.toolsNote, Modifier.fontSize(13.px).color(palette.textSecondary))

    Card {
        SectionTitle(t.budget)
        val q = strings().quality
        // With the generator's detailed model still kept, the budget is cut from it again: up as well as down.
        val hasSource = info?.sourceUrl != null
        val highPoly = triangles > RobloxMeshLimits.MAX_TRIANGLES
        var budget by remember(asset.payloadUrl) { mutableStateOf(minOf(TrianglePrefs.MOB, maxOf(RobloxMeshLimits.MIN_TRIANGLES, triangles - 500))) }
        if (hasSource) SpanText(t.budgetFromSource(info?.sourceTriangles), Modifier.fontSize(12.px).color(palette.textSecondary))
        Chips {
            listOf(TrianglePrefs.MOB to q.trianglesMob, TrianglePrefs.CHARACTER to q.trianglesCharacter, TrianglePrefs.BOSS to q.trianglesBoss)
                .forEach { (value, label) ->
                    Chip("$label · ${t.trianglesValue(value)}", budget == value, enabled = hasSource || value < triangles) { budget = value }
                }
            if (hasSource) Chip(q.trianglesHighPoly, budget == RobloxMeshLimits.KEEP_DETAIL, enabled = !highPoly) { budget = RobloxMeshLimits.KEEP_DETAIL }
        }
        Row(Modifier.fillMaxWidth().gap(10.px), verticalAlignment = Alignment.CenterVertically) {
            Input(
                type = InputType.Range,
                attrs = Modifier.flexGrow(1).toAttrs {
                    attr("min", RobloxMeshLimits.MIN_TRIANGLES.toString())
                    attr("max", RobloxMeshLimits.MAX_TRIANGLES.toString())
                    attr("step", "500")
                    attr("aria-label", t.budget)
                    value(maxOf(budget, RobloxMeshLimits.MIN_TRIANGLES).toString())
                    onInput { event -> event.value?.toInt()?.let { budget = it } }
                }
            )
            SpanText(
                if (budget == RobloxMeshLimits.KEEP_DETAIL) q.trianglesHighPoly else t.trianglesValue(budget),
                Modifier.fontSize(13.px).fontWeight(FontWeight.SemiBold).color(palette.textPrimary).minWidth(96.px)
            )
        }
        AppButton(
            text = t.apply,
            size = AppButtonSize.SM,
            enabled = canEdit && (if (budget == RobloxMeshLimits.KEEP_DETAIL) !highPoly else budget != triangles && (hasSource || budget < triangles)),
            onClick = { onEdit(MeshEditRequest(targetTriangles = budget)) }
        )
    }

    Card {
        SectionTitle(t.sizeTitle)
        SpanText(t.sizeHint, Modifier.fontSize(12.px).color(palette.textSecondary))
        val current = stats.sizeStuds.maxOrNull() ?: 4.0
        var sizeText by remember(asset.payloadUrl, stats.loaded) { mutableStateOf(((current * 10).roundToInt() / 10.0).toString()) }
        val parsed = sizeText.replace(',', '.').toDoubleOrNull()?.takeIf { it in 0.1..2048.0 }
        Row(Modifier.fillMaxWidth().gap(8.px), verticalAlignment = Alignment.CenterVertically) {
            Input(
                type = InputType.Number,
                attrs = Modifier.width(110.px).height(36.px).padding(leftRight = 10.px).borderRadius(9.px)
                    .border(1.px, LineStyle.Solid, palette.borderStrong).backgroundColor(palette.background).color(palette.textPrimary)
                    .styleModifier { property("font-family", "inherit") }
                    .toAttrs {
                        attr("min", "0.1")
                        attr("max", "2048")
                        attr("step", "0.5")
                        attr("aria-label", t.sizeTitle)
                        value(sizeText)
                        onInput { event -> sizeText = event.value?.toString().orEmpty() }
                    }
            )
            SpanText(t.studs(parsed ?: current).substringAfter(' '), Modifier.fontSize(13.px).color(palette.textSecondary))
            Spacer()
            AppButton(
                text = t.apply,
                size = AppButtonSize.SM,
                enabled = canEdit && parsed != null && abs(parsed - current) > 0.05,
                onClick = { parsed?.let { onEdit(MeshEditRequest(sizeStuds = it)) } }
            )
        }
    }

    Card {
        SectionTitle(t.rotateTitle)
        SpanText(t.rotateHint, Modifier.fontSize(12.px).color(palette.textSecondary))
        Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap)) {
            listOf(t.rotateLeft to 90, t.rotateRight to 270, t.rotate180 to 180).forEach { (label, degrees) ->
                AppButton(text = label, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, enabled = canEdit, onClick = {
                    onEdit(MeshEditRequest(rotateDegrees = degrees))
                })
            }
        }
    }

    Card {
        SectionTitle(t.textureTitle)
        val texture = info?.textureSize ?: stats.textureSize
        Chips {
            RobloxMeshLimits.TEXTURE_SIZES.forEach { size ->
                Chip(t.pixels(size), texture in 1..size && texture == size, enabled = canEdit && texture > size) {
                    onEdit(MeshEditRequest(maxTexture = size))
                }
            }
        }
        if (texture <= 0) SpanText(t.checkTextureNone, Modifier.fontSize(12.px).color(palette.textSecondary))
    }

    Card {
        Row(Modifier.fillMaxWidth().gap(10.px), verticalAlignment = Alignment.CenterVertically) {
            Column(Modifier.flexGrow(1).minWidth(0.px).gap(2.px)) {
                SectionTitle(t.debrisTitle)
                SpanText(
                    info?.smallParts?.let { if (it > 0) t.checkParts(it) else t.checkPartsOk } ?: t.debrisHint,
                    Modifier.fontSize(12.px).color(palette.textSecondary)
                )
            }
            AppButton(
                text = t.fixDebris,
                variant = AppButtonVariant.SECONDARY,
                size = AppButtonSize.SM,
                enabled = canEdit && (info?.smallParts ?: 0) > 0,
                onClick = { onEdit(MeshEditRequest(dropSmallParts = true)) }
            )
        }
    }

    Card {
        SectionTitle(t.retextureTitle)
        SpanText(t.retextureHint, Modifier.fontSize(12.px).color(palette.textSecondary))
        var style by remember(asset.id) { mutableStateOf("") }
        var picture by remember(asset.id) { mutableStateOf<String?>(null) }
        val pictureInputId = remember(asset.id) { "retexture-picture-${asset.id}" }
        Chips {
            t.retexturePresets.forEach { preset -> Chip(preset, style == preset) { style = preset } }
        }
        Input(
            type = InputType.Text,
            attrs = Modifier.fillMaxWidth().height(36.px).padding(leftRight = 10.px).borderRadius(9.px)
                .border(1.px, LineStyle.Solid, palette.borderStrong).backgroundColor(palette.background).color(palette.textPrimary)
                .styleModifier { property("font-family", "inherit") }
                .toAttrs {
                    attr("maxlength", "300")
                    attr("placeholder", t.retexturePlaceholder)
                    attr("aria-label", t.retextureTitle)
                    value(style)
                    onInput { event -> style = event.value }
                }
        )
        Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap), verticalAlignment = Alignment.CenterVertically) {
            val chosen = picture
            if (chosen == null) {
                AppButton(text = t.retexturePicture, icon = AppIcon.PAPERCLIP, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, onClick = {
                    (kotlinx.browser.document.getElementById(pictureInputId) as? org.w3c.dom.HTMLInputElement)?.click()
                })
            } else {
                Img(src = chosen, attrs = Modifier.size(36.px).borderRadius(8.px).objectFit(com.varabyte.kobweb.compose.css.ObjectFit.Cover).toAttrs())
                AppButton(text = t.retextureRemovePicture, variant = AppButtonVariant.OUTLINE, size = AppButtonSize.SM, onClick = { picture = null })
            }
            Input(
                type = InputType.File,
                attrs = Modifier.display(DisplayStyle.None).id(pictureInputId).toAttrs {
                    attr("accept", "image/*")
                    onInput { event ->
                        val input = event.target as? org.w3c.dom.HTMLInputElement
                        val file = input?.files?.item(0)
                        if (file != null && file.size.toDouble() <= 5 * 1024 * 1024) {
                            val reader = org.w3c.files.FileReader()
                            reader.onload = { picture = reader.result as? String }
                            reader.readAsDataURL(file)
                        }
                        input?.value = ""
                    }
                }
            )
        }
        AppButton(
            text = t.retextureApply(retextureCost),
            icon = AppIcon.SPARKLES,
            size = AppButtonSize.SM,
            enabled = canEdit && (style.isNotBlank() || picture != null),
            onClick = { onRetexture(RetextureRequest(style = style.trim(), styleImage = picture)) }
        )
    }

    Card {
        SectionTitle(t.rigTitle)
        val rigged = info?.riggedUrl
        val byMeshy = asset.metadata["riggedBy"] == "meshy" && rigged != null
        SpanText(if (rigged != null) (if (byMeshy) "${t.rigByMeshy}. ${t.rigDone}" else t.rigDone) else t.rigHint, Modifier.fontSize(12.px).color(palette.textSecondary))
        if (rigged == null) SpanText(t.rigProHint, Modifier.fontSize(12.px).color(palette.textSecondary))
        Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap)) {
            if (rigged == null) {
                AppButton(text = t.rigApply, icon = AppIcon.FIGURE, size = AppButtonSize.SM, enabled = canEdit, onClick = onRig)
                AppButton(text = t.rigPro(rigProCost), icon = AppIcon.SPARKLES, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, enabled = canEdit, onClick = onRigPro)
            } else {
                AppButton(text = t.rigDownload, icon = AppIcon.DOWNLOAD, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, onClick = {
                    kotlinx.browser.window.open(BackendEndpoint.resolveAssetUrl(rigged), "_blank")
                })
            }
        }
    }

    Card {
        SectionTitle(t.downloadTitle)
        Row(Modifier.fillMaxWidth().gap(8.px).flexWrap(FlexWrap.Wrap)) {
            AppButton(text = t.downloadGlb, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, onClick = onDownloadGlb)
            AppButton(text = t.downloadObj, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, enabled = info?.editable == true, onClick = onDownloadObj)
            // Roblox gets 1024 px; a sharper copy for other tools is baked from the detailed source while it is kept.
            if (info?.sourceUrl != null && triangles <= RobloxMeshLimits.MAX_TRIANGLES) {
                AppButton(text = t.downloadSharp, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, enabled = !busy, onClick = onDownloadSharp)
            }
            info?.sourceUrl?.let { url ->
                AppButton(text = t.downloadHighPoly, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, onClick = {
                    kotlinx.browser.window.open(BackendEndpoint.resolveAssetUrl(url), "_blank")
                })
            }
        }
    }

    val versions = asset.revisions.filter { it.payloadUrl != null }.sortedByDescending { it.revisionNumber }
    if (versions.size > 1) {
        Card {
            SectionTitle(t.versions)
            val current = asset.metadata["currentRevision"]?.toIntOrNull() ?: versions.first().revisionNumber
            versions.forEach { rev ->
                val isCurrent = rev.revisionNumber == current
                Row(
                    Modifier.fillMaxWidth().gap(8.px).padding(8.px).borderRadius(10.px)
                        .border(1.px, LineStyle.Solid, if (isCurrent) palette.accent else palette.border),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(t.versionLabel(rev.revisionNumber), Modifier.fontSize(13.px).fontWeight(FontWeight.Bold).color(palette.textPrimary))
                    Column(Modifier.flexGrow(1).minWidth(0.px).gap(2.px)) {
                        SpanText(describeEdit(rev.metadata["meshEdit"], t), Modifier.fontSize(12.px).color(palette.textPrimary))
                        val tris = rev.metadata["polyCount"]?.toIntOrNull()
                        SpanText(
                            listOfNotNull(
                                tris?.let { t.trianglesValue(it) },
                                if (isCurrent) t.current else null
                            ).joinToString(" · "),
                            Modifier.fontSize(11.px).color(if (isCurrent) palette.accentText else palette.textSecondary)
                        )
                    }
                    if (!isCurrent) {
                        AppButton(text = t.compare, variant = AppButtonVariant.GHOST, size = AppButtonSize.SM, enabled = !busy, onClick = { onCompare(rev) })
                        AppButton(text = t.restore, variant = AppButtonVariant.SECONDARY, size = AppButtonSize.SM, enabled = !busy, onClick = { onRestore(rev.revisionNumber) })
                    }
                }
            }
        }
    }
}

/** "triangles=5000;rotate=90" -> "simplified to 5,000 tris, turned 90°". */
fun describeEdit(code: String?, t: MeshInspectorStrings): String {
    if (code.isNullOrBlank()) return t.originalVersion
    return code.split(';').mapNotNull { part ->
        val key = part.substringBefore('=')
        val value = part.substringAfter('=', "")
        when (key) {
            "triangles" -> value.toIntOrNull()?.let { t.editTriangles(it) }
            "highpoly" -> t.editHighPoly
            "retexture" -> t.editRetexture
            "size" -> value.toDoubleOrNull()?.let { t.editSize(t.studs(it)) }
            "rotate" -> value.toIntOrNull()?.let { t.editRotate(if (it == 270) 90 else it) }
            "texture" -> value.toIntOrNull()?.let { t.editTexture(it) }
            "debris" -> t.editDebris
            else -> null
        }
    }.joinToString(", ").replaceFirstChar { it.uppercase() }
}

// ─── Small building blocks ──────────────────────────────────────────────────────────────────────────────────────

@Composable
private fun Card(content: @Composable () -> Unit) {
    val palette = LocalAppTheme.current.colors
    Column(
        Modifier.fillMaxWidth().gap(10.px).padding(14.px).borderRadius(12.px)
            .backgroundColor(palette.background).border(1.px, LineStyle.Solid, palette.border)
    ) { content() }
}

@Composable
private fun SectionTitle(text: String) {
    val palette = LocalAppTheme.current.colors
    SpanText(text, Modifier.fontSize(13.px).fontWeight(FontWeight.Bold).color(palette.textPrimary))
}

@Composable
private fun InfoRow(label: String, value: String) {
    val palette = LocalAppTheme.current.colors
    Row(Modifier.fillMaxWidth().gap(12.px), verticalAlignment = Alignment.CenterVertically) {
        SpanText(label, Modifier.fontSize(13.px).color(palette.textSecondary).width(120.px).flexShrink(0))
        SpanText(value, Modifier.fontSize(13.px).color(palette.textPrimary).fontWeight(FontWeight.SemiBold))
    }
}

@Composable
private fun Note(text: String, color: Color) {
    SpanText(
        text,
        Modifier.fillMaxWidth().padding(10.px).borderRadius(10.px).fontSize(13.px)
            .color(color).backgroundColor(color.toRgb().copy(alpha = 28))
    )
}

@Composable
private fun Chips(content: @Composable () -> Unit) {
    Row(Modifier.fillMaxWidth().gap(6.px).flexWrap(FlexWrap.Wrap)) { content() }
}

@Composable
private fun Chip(label: String, selected: Boolean, enabled: Boolean = true, swatch: Color? = null, onClick: () -> Unit) {
    val palette = LocalAppTheme.current.colors
    Button(
        Modifier
            .height(32.px)
            .padding(leftRight = 12.px)
            .gap(6.px)
            .borderRadius(999.px)
            .border(1.px, LineStyle.Solid, if (selected) palette.accent else palette.borderStrong)
            .backgroundColor(if (selected) palette.accent.toRgb().copy(alpha = 48) else Colors.Transparent)
            .color(if (enabled) palette.textPrimary else palette.textSecondary)
            .opacity(if (enabled) 1.0 else 0.5)
            .fontSize(12.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
            .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
            .styleModifier { property("font-family", "inherit") }
            .onClick { if (enabled) onClick() }
            .toAttrs {
                attr("type", "button")
                attr("aria-pressed", selected.toString())
                if (!enabled) attr("disabled", "")
            }
    ) {
        if (swatch != null) Box(Modifier.size(12.px).borderRadius(4.px).backgroundColor(swatch).border(1.px, LineStyle.Solid, palette.borderStrong))
        Text(label)
    }
}

@Composable
private fun Segment(label: String, selected: Boolean, modifier: Modifier = Modifier, onClick: () -> Unit) {
    val palette = LocalAppTheme.current.colors
    Button(
        modifier
            .height(34.px)
            .borderRadius(8.px)
            .border(0.px, LineStyle.None, Colors.Transparent)
            .backgroundColor(if (selected) palette.surfaceElevated else Colors.Transparent)
            .color(if (selected) palette.textPrimary else palette.textSecondary)
            .fontSize(13.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .styleModifier { property("font-family", "inherit") }
            .onClick { onClick() }
            .toAttrs {
                attr("type", "button")
                attr("aria-pressed", selected.toString())
            }
    ) { Text(label) }
}

@Composable
private fun Toggle(label: String, hint: String?, checked: Boolean, onChange: (Boolean) -> Unit) {
    val palette = LocalAppTheme.current.colors
    Row(
        Modifier.fillMaxWidth().gap(10.px).cursor(Cursor.Pointer).onClick { onChange(!checked) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        Column(Modifier.flexGrow(1).minWidth(0.px).gap(2.px)) {
            SpanText(label, Modifier.fontSize(13.px).color(palette.textPrimary))
            hint?.let { SpanText(it, Modifier.fontSize(12.px).color(palette.textSecondary)) }
        }
        Box(
            Modifier.width(38.px).height(22.px).borderRadius(999.px).flexShrink(0).padding(2.px)
                .backgroundColor(if (checked) palette.accent else palette.borderStrong)
                .attrsModifier {
                    attr("role", "switch")
                    attr("aria-checked", checked.toString())
                    attr("aria-label", label)
                },
            contentAlignment = if (checked) Alignment.CenterEnd else Alignment.CenterStart
        ) {
            Box(Modifier.size(18.px).borderRadius(999.px).backgroundColor(Colors.White))
        }
    }
}
